import { useQuery } from '@tanstack/react-query';
import type { OAuthRedirectUris } from '@mg/shared';
import { api } from '../../lib/api';

/** One answer for the whole app: the URLs belong to the install, not to a workspace or a user. */
export const OAUTH_REDIRECT_URIS_QUERY_KEY = ['social', 'oauth-redirect-uris'] as const;

/**
 * The callback URLs as the connect dialogs worked them out before the server was asked: the
 * page's own host. Used only when the server cannot be asked.
 */
export function redirectUrisOnHost(origin: string): OAuthRedirectUris {
  return {
    facebook: `${origin}/api/social/facebook/callback`,
    linkedin: `${origin}/api/social/linkedin/callback`,
    instagram: `${origin}/api/social/oauth/instagram/callback`,
    threads: `${origin}/api/social/oauth/threads/callback`,
    x: `${origin}/api/social/oauth/x/callback`,
  };
}

/**
 * The OAuth callback URL the server sends to each platform, for the setup guides to show: the URL the
 * install's developer app must register (Site Integrations reads its own server view).
 *
 * Asked of the server rather than worked out from the page's address, because the server sends one
 * configured URL per platform whichever host the app is browsed on: a URL built from the page's
 * host is wrong on every host but that one.
 *
 * - `null` until the server answers: show a neutral "Loading…", never a guessed URL.
 * - The server's URLs once it has. They cannot change while the app is open, so they are asked for
 *   once.
 * - If the server cannot be asked, the page's own host, as the dialogs showed before.
 *
 * The request carries no sign-in and no workspace: the guides open in a new tab, outside the
 * sign-in. `enabled: false` (a caller not showing them yet) asks for nothing yet.
 */
export function useOAuthRedirectUris({ enabled = true }: { enabled?: boolean } = {}): OAuthRedirectUris | null {
  const { data, isError } = useQuery({
    queryKey: OAUTH_REDIRECT_URIS_QUERY_KEY,
    queryFn: ({ signal }) => api.getOAuthRedirectUris(signal),
    staleTime: Infinity,
    enabled,
  });
  if (data) return data;
  return isError ? redirectUrisOnHost(window.location.origin) : null;
}
